import { useEffect, useRef, useState } from "react";

export interface LoadingStep {
  /** Text shown as the step label. */
  label: string;
  /**
   * Short fun messages that cycle every 700 ms while this step is active.
   * At least one message is required. If only one is provided, cycling is skipped.
   */
  subMessages: string[];
  /** Milliseconds from mount before this step becomes active. 0 for the first step. */
  delay: number;
}

interface UseLoadingStepsResult {
  activeStepIndex: number;
  subMessage: string;
}

/**
 * Runs a simulated loading step sequence with fixed delays.
 * The last step stays active indefinitely, cycling its sub-messages,
 * until the component unmounts.
 */
export function useLoadingSteps(steps: LoadingStep[]): UseLoadingStepsResult {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [subMessageIndex, setSubMessageIndex] = useState(0);
  const mountTimeRef = useRef(Date.now());

  // Schedule step transitions on mount
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    steps.forEach((step, i) => {
      if (i === 0) return;
      const elapsed = Date.now() - mountTimeRef.current;
      const remaining = Math.max(0, step.delay - elapsed);
      const t = setTimeout(() => {
        setActiveStepIndex(i);
        setSubMessageIndex(0);
      }, remaining);
      timers.push(t);
    });

    return () => timers.forEach(clearTimeout);
  }, [steps]);

  // Cycle sub-messages for the active step
  useEffect(() => {
    const currentStep = steps[activeStepIndex];
    if (!currentStep || currentStep.subMessages.length <= 1) return;

    const interval = setInterval(() => {
      setSubMessageIndex((i) => (i + 1) % currentStep.subMessages.length);
    }, 700);

    return () => clearInterval(interval);
  }, [activeStepIndex, steps]);

  const subMessage = steps[activeStepIndex]?.subMessages[subMessageIndex] ?? "";

  return { activeStepIndex, subMessage };
}
